import * as moment from "moment-timezone";
import {useLazyQuery, useQuery} from "@apollo/client";
import {useCallback, useEffect, useMemo, useState} from "react";

import {
    ALL_SERVICE_TYPES,
    QUERY_CAN_TEACH_DOMAIN_USERS_AND_THEIR_AVAILABILITY_FILTERED_BY_TIME,
    QUERY_CAN_TEACH_DOMAIN_USERS_AND_THEIR_SHIFT_DATA_FILTERED_BY_TIME,
    QUERY_CAN_TEACH_DOMAIN_USERS_AND_THEIR_TIMESLOT_FILTERED_BY_TIME
} from "../queries/GraphQLQueries";
import buildUserShiftOrTimeslotData from "../helpers/ShiftHelper";
import {ScheduleViewTypes} from "../domain/ScheduleViewTypes";

const HOURS_DIFFERENCE_THRESHOLD_FOR_QUERYING_ONLY_SHIFT = 24*7;
const HOURS_DIFFERENCE_THRESHOLD_FOR_QUERYING_ONLY_TIMESLOTS = 18;

export default function usePerformerShifts() {

    const [shiftData, setShiftData] = useState([]);
    const [isDataDirty, setIsDataDirty] = useState(false);
    const [lastDataBounds, setLastDataBounds] = useState({});
    const [lastVisibleBounds, setLastVisibleBounds] = useState({});
    const [currentViewType, setCurrentViewType] = useState(ScheduleViewTypes.SHIFT_ONLY);
    const [areShiftsFiltered, setAreShiftsFiltered] = useState(true);
    const [selectedServiceTypeKeycodes, setSelectedServiceTypeKeycodes] = useState([]);
    const [isServiceTypeFilterInitialized, setIsServiceTypeFilterInitialized] = useState(false);
    const {data: serviceTypesData} = useQuery(ALL_SERVICE_TYPES);
    const serviceTypeOptions = useMemo(() => serviceTypesData?.serviceTypes || [], [serviceTypesData]);

    useEffect(() => {
        if (isServiceTypeFilterInitialized || serviceTypeOptions.length === 0) {
            return;
        }

        const defaultVisibleServiceTypeKeycodes = serviceTypeOptions
            .filter((serviceType) => serviceType.isExternal === false)
            .map((serviceType) => serviceType.keycode);

        setSelectedServiceTypeKeycodes(defaultVisibleServiceTypeKeycodes);
        setIsServiceTypeFilterInitialized(true);
    }, [isServiceTypeFilterInitialized, serviceTypeOptions]);

    const [fetchPerformerTimeslotsIncludingBookings, {loading: loadingTimeslotsIncludingBookings,  data: performerTimeslotsIncludingBookings}] = useLazyQuery(QUERY_CAN_TEACH_DOMAIN_USERS_AND_THEIR_AVAILABILITY_FILTERED_BY_TIME,
        { fetchPolicy: 'network-only' }
    );

    const [fetchPerformerTimeslotsExcludingBookings , {loading: loadingTimeslotsExcludingBookings,  data: performerTimeslotsExcludingBookings}] = useLazyQuery(QUERY_CAN_TEACH_DOMAIN_USERS_AND_THEIR_TIMESLOT_FILTERED_BY_TIME,
        { fetchPolicy: 'network-only' }
    );

    const [fetchPerformerShifts , {loading: loadingShiftData,  data: performerShifts}] = useLazyQuery(QUERY_CAN_TEACH_DOMAIN_USERS_AND_THEIR_SHIFT_DATA_FILTERED_BY_TIME,
        { fetchPolicy: 'network-only' }
    );

    const filterAndProcessPerformerShiftData = useCallback((performerTimeslots, viewType) => {
        if (performerTimeslots?.users_filtered) {
            const filteredPeriods = performerTimeslots
                .users_filtered
                .flatMap(performer => buildUserShiftOrTimeslotData(performer, viewType, performerTimeslots.serviceTypes));
            setShiftData(filteredPeriods);
        }
    }, []);

    useEffect(() => {
        if (currentViewType === ScheduleViewTypes.DETAILED) {
            filterAndProcessPerformerShiftData(performerTimeslotsIncludingBookings, ScheduleViewTypes.DETAILED);
        }
    }, [performerTimeslotsIncludingBookings, currentViewType, filterAndProcessPerformerShiftData]);

    useEffect(() => {
        if (currentViewType === ScheduleViewTypes.TIMESLOTS_ONLY) {
            filterAndProcessPerformerShiftData(performerTimeslotsExcludingBookings, ScheduleViewTypes.TIMESLOTS_ONLY);
        }
    }, [performerTimeslotsExcludingBookings, currentViewType, filterAndProcessPerformerShiftData]);

    useEffect(() => {
        if (currentViewType === ScheduleViewTypes.SHIFT_ONLY) {
            filterAndProcessPerformerShiftData(performerShifts, ScheduleViewTypes.SHIFT_ONLY);
        }
    }, [performerShifts, currentViewType, filterAndProcessPerformerShiftData]);

    useEffect(() => {
        if (lastDataBounds.startTime && lastDataBounds.endTime) {
            setCursorStyle('wait');
            loadShiftAndPeriodData(lastDataBounds.startTime, lastDataBounds.endTime);
        }
    }, [areShiftsFiltered, selectedServiceTypeKeycodes]);

    const isAnyDataLoading = loadingTimeslotsIncludingBookings || loadingShiftData || loadingTimeslotsExcludingBookings;

    const setCursorStyle = (style) => document.body.style.cursor = style;

    const handleManualRefresh = useCallback(() => {
        if (isAnyDataLoading) {
            setIsDataDirty(true);
            return;
        }

        setCursorStyle('wait');
        loadShiftAndPeriodData(lastDataBounds.startTime, lastDataBounds.endTime);
    }, [isAnyDataLoading, lastDataBounds]);

    const handleTimelineBoundsChanged = useCallback((canvasTimeStart, canvasTimeEnd) => {

        if (!lastVisibleBounds.visibleTimeStart) {
            setLastVisibleBounds({visibleTimeStart: canvasTimeStart, visibleTimeEnd: canvasTimeEnd});
        }

        const startTime = moment(canvasTimeStart).toISOString();
        const endTime = moment(canvasTimeEnd).toISOString();

        setLastDataBounds({startTime, endTime});
        if (isAnyDataLoading) {
            // prevent fetching data if we're already loading, otherwise we'll kill the server
            setIsDataDirty(true);
            return;
        }

        setCursorStyle('wait');
        loadShiftAndPeriodData(startTime, endTime);
    }, [isAnyDataLoading, lastVisibleBounds]);

    const handleTimelineTimeChanged = useCallback((visibleTimeStart, visibleTimeEnd, updateScrollCanvas, unit) => {
        updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
        setLastVisibleBounds({visibleTimeStart, visibleTimeEnd});
    }, []);

    const processPerformerShiftData = useCallback((data, viewType) => {
        if (data) {
            filterAndProcessPerformerShiftData(data, viewType);
        }
    }, [filterAndProcessPerformerShiftData]);

    const determineAvailabilityQuery = useCallback((viewType) => {
        let availabilityFetcher;

        if (viewType === ScheduleViewTypes.SHIFT_ONLY) {
            availabilityFetcher = fetchPerformerShifts;
        } else if (viewType === ScheduleViewTypes.TIMESLOTS_ONLY) {
            availabilityFetcher = fetchPerformerTimeslotsExcludingBookings;
        } else {
            availabilityFetcher = fetchPerformerTimeslotsIncludingBookings;
        }

        return availabilityFetcher;
    }, [fetchPerformerShifts, fetchPerformerTimeslotsExcludingBookings, fetchPerformerTimeslotsIncludingBookings]);

    const buildQueryVariables = useCallback((fromTime, toTime) => {
        const queryVariables = {
            fromTime,
            toTime,
            includeOnlyAccreditedServiceTypes: areShiftsFiltered
        };

        if (isServiceTypeFilterInitialized) {
            queryVariables.serviceTypeKeyCodes = selectedServiceTypeKeycodes;
        }

        return queryVariables;
    }, [areShiftsFiltered, selectedServiceTypeKeycodes, isServiceTypeFilterInitialized]);

    const loadShiftAndPeriodData = useCallback((startTime, endTime) => {
        const wasDataDirty = isDataDirty;
        setIsDataDirty(false);

        const viewType = determineAvailabilityViewType(startTime, endTime);
        setCurrentViewType(viewType);

        const performerShiftQuery =  determineAvailabilityQuery(viewType);

        let fromTime = startTime;
        let toTime = endTime;

        const isVisibleBoundsSame = moment(lastVisibleBounds.visibleTimeStart).isSame(moment(lastVisibleBounds.visibleTimeEnd));

        if (isVisibleBoundsSame) {
            setLastVisibleBounds({visibleTimeStart: startTime, visibleTimeEnd: endTime});

            const expandedTimes = expandTimeRange(startTime, endTime);
            fromTime = expandedTimes.expandedStartTime;
            toTime = expandedTimes.expandedEndTime;
        }

        performerShiftQuery({ variables: buildQueryVariables(fromTime, toTime) })
            .then(result => {
                processPerformerShiftData(result.data, viewType);
            })
            .finally(() => {
                setCursorStyle('default');
                if (wasDataDirty) {
                    performerShiftQuery({ variables: buildQueryVariables(lastDataBounds.startTime, lastDataBounds.endTime) })
                        .then(result => {
                            processPerformerShiftData(result.data, viewType);
                        });
                }
            });
    }, [lastVisibleBounds, currentViewType, lastDataBounds, processPerformerShiftData, determineAvailabilityQuery, isDataDirty, buildQueryVariables]);

    const expandTimeRange = useCallback((startTime, endTime) => {
        const start = moment(startTime);
        const end = moment(endTime);

        const timeIntervalBetweenBounds = moment.duration(end.diff(start));

        const expandedStartTime = start.subtract(timeIntervalBetweenBounds);
        const expandedEndTime = end.add(timeIntervalBetweenBounds);

        return {expandedStartTime, expandedEndTime};
    }, []);

    const determineAvailabilityViewType = (startTime, endTime) => {
        const timeDifference = moment(endTime).diff(startTime, 'hours');

        if (timeDifference > HOURS_DIFFERENCE_THRESHOLD_FOR_QUERYING_ONLY_SHIFT) {
            return ScheduleViewTypes.SHIFT_ONLY;
        } else if (timeDifference > HOURS_DIFFERENCE_THRESHOLD_FOR_QUERYING_ONLY_TIMESLOTS) {
            return ScheduleViewTypes.TIMESLOTS_ONLY;
        } else {
            return ScheduleViewTypes.DETAILED;
        }
    };

    return {
        handleManualRefresh,
        handleTimelineBoundsChanged,
        handleTimelineTimeChanged,
        setAreShiftsFiltered,
        areShiftsFiltered,
        isShiftDataLoading: isAnyDataLoading,
        shiftData,
        currentViewType,
        lastVisibleBounds,
        lastDataBounds,
        serviceTypeOptions,
        selectedServiceTypeKeycodes,
        setSelectedServiceTypeKeycodes
    };
}